import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LogoIcon from "../../../assets/images/logo-icon.png";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import variables from "../../../assets/styles/variables.module.scss";
import TypingIndicator from "../../../Shared-ui/SharedComponents/TypingIndicator";
import LineMdAccount from "../../../assets/icons/LineMdAccount";
import { Params, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { Agent } from "../../../Shared-ui/Types/Agent";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { AGENT, AGENT_SHORTCUT, TEAMS } from "../../../Services/Endpoint";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import PlayGroundInitialMessage from "../../Components/PlayGroundIntialMessage";
import { Message, UserMessage } from "../../../Shared-ui/Types/PlayGround";
import { v4 as uuidv4 } from "uuid";
import { PlayGroundEnum } from "../../../Shared-ui/Enum/PlayGroundEnum";
import { GetSession } from "../../../Shared-ui/utils/Auth/AuthHelpers";
import { MultiAgent } from "../../../Shared-ui/Types/MultiAgent";
import { marked } from "marked";
import AgentPlayGroundTools from "../../Components/AgentPlayGroundTools";
import AgentPlaygroundReferences from "../../Components/AgentPlaygroundReferences";
import AgentQuestions from "../../Components/AgentQuestions";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CachedIcon from "@mui/icons-material/Cached";
import ChatInterfaceSettings from "../../Components/ChatInterfaceSettings";
import ChatIcon from "../../../assets/images/AgentStar.svg";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import NewChatIcon from "../../../assets/icons/NewChatIcon";
import EndChatIcon from "../../../assets/icons/EndChatIcon";
import RecentChatIcon from "../../../assets/icons/RecentChatIcon";
import RecentChats from "../../Components/RecentChats";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PlotlyChart from "../../Components/PlotlyChart";
import AgentPrompts from "../../Components/AgentPromts";
import striptags from "striptags";
import { capitalizeFirstLetter } from "../../../Shared-ui/utils/Common/Common";



const MultiAgentPlayground: React.FC = () => {
  const { teamsId } = useParams<Params>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const uniqueId = uuidv4();
  const [intialMessage, setIntialMessage] = useState<string>("");
  const [agents, setAgents] = useState<MultiAgent>();
  const [userInput, setUserInput] = useState<string>("");
  const [botTyping, setBotTyping] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const [conversationId, setConversationId] = useState<string>("");
  const [agentShortuts, setAgentShortuts] = useState<any>([]);
  const [showQuestions, setShowQuestions] = useState<boolean>(true);
  const [showInitialMessage, setShowInitialMessage] = useState(true);
  const [chatIcon, setChatIcon] = useState<string | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [bubbleColor, setBubbleColor] = useState(variables.primaryColor);
  const [incomingTextColor, setIncomingTextColor] = useState("#000000");
  const [outgoingTextColor, setOutgoingTextColor] = useState("#000000");
  const [titleColor, setTitleColor] = useState("#000000");
  const [chatWindowColor, setChatWindowColor] = useState("#ffffff");

  useEffect(() => {
    const scrollBehavior = botTyping ? "auto" : "smooth";
    endOfMessagesRef.current?.scrollIntoView({ behavior: scrollBehavior });
  }, [messages.length, botTyping]);

  useEffect(() => {
    getAgent();
    getAgentShortCut()
  }, [teamsId]);

  useEffect(() => {
    if (!botTyping) {
      inputRef.current?.focus();
    }
  }, [messages, botTyping]);

  const getAgent = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(`${TEAMS}/${teamsId}`).then(
        (res) => res?.data
      );
      // setIntialMessage(`Hello. I am ${data.name}! How can I assist you today?`);
      setIntialMessage(`Hello! How can I assist you today?`);
      setAgents(data);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  };


  const sendMessageShortcuts = async (shortcut: string, messageId: number) => {
    setShowQuestions(false);
    const message = messages[messageId]?.agentData?.agent_responses
    const agents_id: string[] = []
    message?.map((item: any) => {
      if (item?.agent_id && item?.agent_id) {
        agents_id.push(item?.agent_id)
      }
    })

    const original_content = message?.reduce<any>((acc, item: any) => {
      if (item?.agent_name && item?.full_response) {
        acc[item?.agent_name] = item?.full_response || "";
      }
      return acc;
    }, {});
    
    if (original_content && Object.keys(original_content).length === 0) return;

    const data: UserMessage = {
      action: shortcut,
      original_content: original_content,
      conversation_id: conversationId ? conversationId : "",
      agents_id: agents_id,
    };

    // Add user message
    setMessages((prev) => [
      ...prev,
      { sender: PlayGroundEnum.USER, userData: { ...data, input: `<i>${capitalizeFirstLetter(shortcut)}</i>` } },
    ]);
    setUserInput("");
    setBotTyping(true);
    try {
      if (!agents?.team_metadata.streaming) {
        const response = await fetch(
          `${TEAMS}/${teamsId}/get-post-response-shortcuts?stream=true`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${GetSession()}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify(data),
            mode: "cors",
            credentials: "same-origin",
          }
        );

        if (!response.ok || !response.body) {
          throw new Error("Failed to stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        // Add placeholder agent message
        setMessages((prev) => [...prev, { sender: PlayGroundEnum.AGENT }]);

        let buffer = "";
        var content = "";
        const tool_calls: any[] = [];
        const citations: any[] = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const jsonPart = line.replace(/^data: /, "").trim();

              if (!jsonPart || jsonPart === "[DONE]") return;

              try {
                const parsed = JSON.parse(jsonPart);
                if (!parsed.is_complete) {
                  content += parsed.response;
                  const sanitizedContent = await marked.parse(content);
                  setConversationId(parsed.conversation_id);

                  setMessages((preVal) => {
                    let lastMsg = preVal[preVal.length - 1];
                    lastMsg = {
                      ...lastMsg,
                      agentData: { response: sanitizedContent },
                    };
                    return [...preVal.slice(0, preVal.length - 1), lastMsg];
                  });
                } else {
                  // Handle chart data if present
                  if (parsed.is_chart && parsed.response) {
                    try {
                      const chartData = JSON.parse(parsed.response);
                      setMessages((preVal) => {
                        let lastMsg = preVal[preVal.length - 1];
                        lastMsg = {
                          ...lastMsg,
                          agentData: {
                            ...lastMsg.agentData,
                            chartData: chartData,
                            response: "",
                            citations: citations,
                          },
                        };
                        return [...preVal.slice(0, preVal.length - 1), lastMsg];
                      });
                    } catch (chartError) {
                      console.error("Failed to parse chart data:", chartError);
                    }
                  }
                  if (parsed?.metadata?.agent_responses) {
                    const agent_responses = parsed?.metadata.agent_responses;
                    agent_responses?.forEach((agent_res: any) => {
                      if (agent_res.metadata?.tool_calls) {
                        tool_calls.push(...agent_res.metadata.tool_calls);
                      }
                    });
                    setMessages((preVal) => {
                      let lastMsg = preVal[preVal.length - 1];
                      lastMsg = {
                        ...lastMsg,
                        agentData: {
                          ...lastMsg.agentData,
                          response: lastMsg.agentData?.response || "",
                          agent_responses: agent_responses,
                          metadata: { tool_calls: tool_calls },
                        },
                      };
                      return [...preVal.slice(0, preVal.length - 1), lastMsg];
                    });
                  }
                }
              } catch (e) {
                console.error("Parse error:", jsonPart, e);
              }
            }
          }

          // Keep partial buffer (in case a line is split across chunks)
          buffer = lines[lines.length - 1];
        }
      } else {
        const resData = await fetch(
          `${TEAMS}/${teamsId}/get-post-response-shortcuts?stream=false`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${GetSession()}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify(data),
            mode: "cors",
            credentials: "same-origin",
          }
        );

        if (!resData.ok) {
          throw new Error("Failed to response");
        }
        const responseData = await resData.json();
        setConversationId(responseData.conversation_id);
        const rawHtml: string = await marked.parse(responseData.response);
        const tool_calls: any[] = [];
        responseData?.agent_responses?.forEach((agent_res: any) => {
          if (agent_res.metadata?.tool_calls) {
            tool_calls.push(...agent_res.metadata.tool_calls);
          }
        });

        // Handle chart data if present
        if (responseData.is_chart && responseData.response) {
          try {
            const chartData = JSON.parse(responseData.response);
            setMessages((preVal) => [
              ...preVal,
              {
                sender: PlayGroundEnum.AGENT,
                agentData: {
                  response: "",
                  chartData: chartData,
                  agent_responses: responseData.agent_responses,
                  metadata: { tool_calls: tool_calls },
                },
              },
            ]);
          } catch (chartError) {
            console.error("Failed to parse chart data:", chartError);
          }
        } else {
          setMessages((preVal) => [
            ...preVal,
            {
              sender: PlayGroundEnum.AGENT,
              agentData: {
                response: rawHtml,
                agent_responses: responseData.agent_responses,
                metadata: { tool_calls: tool_calls },
              },
            },
          ]);
        }
      }
    } catch (err) {
      console.error("Streaming failed:", err);
    } finally {
      setBotTyping(false);
    }
  };



  const sendMessageQuestions = async (question: string) => {
    setShowInitialMessage(false);
    setShowQuestions(false);
    const data: UserMessage = {
      input: question,
      conversation_id: "",
    };

    // Add user message
    setMessages((prev) => [
      ...prev,
      { sender: PlayGroundEnum.USER, userData: data },
    ]);
    setUserInput("");
    setBotTyping(true);
    try {
      if (!agents?.team_metadata.streaming) {
        const response = await fetch(
          `${TEAMS}/${teamsId}/execute?stream=true`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${GetSession()}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify(data),
            mode: "cors",
            credentials: "same-origin",
          }
        );

        if (!response.ok || !response.body) {
          throw new Error("Failed to stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        // Add placeholder agent message
        setMessages((prev) => [...prev, { sender: PlayGroundEnum.AGENT }]);

        let buffer = "";
        var content = "";
        const tool_calls: any[] = [];
        const citations: any[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const jsonPart = line.replace(/^data: /, "").trim();

              if (!jsonPart || jsonPart === "[DONE]") return;

              try {
                const parsed = JSON.parse(jsonPart);
                if (!parsed.is_complete) {
                  content += parsed.response;
                  const sanitizedContent = await marked.parse(content);
                  setConversationId(parsed.conversation_id);

                  setMessages((preVal) => {
                    let lastMsg = preVal[preVal.length - 1];
                    lastMsg = {
                      ...lastMsg,
                      agentData: { response: sanitizedContent },
                    };
                    return [...preVal.slice(0, preVal.length - 1), lastMsg];
                  });
                } else {
                  // Handle chart data if present
                  if (parsed.is_chart && parsed.response) {
                    try {
                      const chartData = JSON.parse(parsed.response);
                      setMessages((preVal) => {
                        let lastMsg = preVal[preVal.length - 1];
                        lastMsg = {
                          ...lastMsg,
                          agentData: {
                            ...lastMsg.agentData,
                            chartData: chartData,
                            response: "",
                            citations: citations,
                          },
                        };
                        return [...preVal.slice(0, preVal.length - 1), lastMsg];
                      });
                    } catch (chartError) {
                      console.error("Failed to parse chart data:", chartError);
                    }
                  }
                  if (parsed?.metadata?.agent_responses) {
                    const agent_responses = parsed?.metadata.agent_responses;
                    agent_responses?.forEach((agent_res: any) => {
                      if (agent_res.metadata?.tool_calls) {
                        tool_calls.push(...agent_res.metadata.tool_calls);
                      }
                    });


                    if (parsed?.citations) {
                      citations.push(...parsed.citations);
                    }

                    setMessages((preVal) => {
                      let lastMsg = preVal[preVal.length - 1];
                      lastMsg = {
                        ...lastMsg,
                        agentData: {
                          ...lastMsg.agentData,
                          response: lastMsg.agentData?.response || "",
                          agent_responses: agent_responses,
                          metadata: { tool_calls: tool_calls },
                          citations: citations,
                        },
                      };
                      return [...preVal.slice(0, preVal.length - 1), lastMsg];
                    });
                  }
                }
              } catch (e) {
                console.error("Parse error:", jsonPart, e);
              }
            }
          }

          // Keep partial buffer (in case a line is split across chunks)
          buffer = lines[lines.length - 1];
        }
      } else {
        const resData = await fetch(
          `${TEAMS}/${teamsId}/execute?stream=false`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${GetSession()}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify(data),
            mode: "cors",
            credentials: "same-origin",
          }
        );

        if (!resData.ok) {
          throw new Error("Failed to response");
        }
        const responseData = await resData.json();
        setConversationId(responseData.conversation_id);
        const rawHtml: string = await marked.parse(responseData.response);
        const tool_calls: any[] = [];
        responseData?.agent_responses?.forEach((agent_res: any) => {
          if (agent_res.metadata?.tool_calls) {
            tool_calls.push(...agent_res.metadata.tool_calls);
          }
        });

        // Handle chart data if present
        if (responseData.is_chart && responseData.response) {
          try {
            const chartData = JSON.parse(responseData.response);
            setMessages((preVal) => [
              ...preVal,
              {
                sender: PlayGroundEnum.AGENT,
                agentData: {
                  response: "",
                  chartData: chartData,
                  agent_responses: responseData.agent_responses,
                  metadata: { tool_calls: tool_calls },
                },
              },
            ]);
          } catch (chartError) {
            console.error("Failed to parse chart data:", chartError);
          }
        } else {
          setMessages((preVal) => [
            ...preVal,
            {
              sender: PlayGroundEnum.AGENT,
              agentData: {
                response: rawHtml,
                agent_responses: responseData.agent_responses,
                metadata: { tool_calls: tool_calls },
              },
            },
          ]);
        }
      }
    } catch (err) {
      console.error("Streaming failed:", err);
    } finally {
      setBotTyping(false);
    }
  };

  const sendMessage = async () => {
    if (!userInput.trim()) return; // Hide Welcome messages
    setShowInitialMessage(false); // Hide Welcome messages
    setShowQuestions(false);
    const data: UserMessage = {
      input: userInput,
      conversation_id: "",
    };

    // Add user message
    setMessages((prev) => [
      ...prev,
      { sender: PlayGroundEnum.USER, userData: data },
    ]);
    setUserInput("");
    setBotTyping(true);
    try {
      if (!agents?.team_metadata.streaming) {
        const response = await fetch(
          `${TEAMS}/${teamsId}/execute?stream=true`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${GetSession()}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify(data),
            mode: "cors",
            credentials: "same-origin",
          }
        );

        if (!response.ok || !response.body) {
          throw new Error("Failed to stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        // Add placeholder agent message
        setMessages((prev) => [...prev, { sender: PlayGroundEnum.AGENT }]);

        let buffer = "";
        var content = "";
        const tool_calls: any[] = [];
        const citations: any[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const jsonPart = line.replace(/^data: /, "").trim();

              if (!jsonPart || jsonPart === "[DONE]") return;

              try {
                const parsed = JSON.parse(jsonPart);
                if (!parsed.is_complete) {
                  content += parsed.response;
                  const sanitizedContent = await marked.parse(content);
                  setConversationId(parsed.conversation_id);

                  setMessages((preVal) => {
                    let lastMsg = preVal[preVal.length - 1];
                    lastMsg = {
                      ...lastMsg,
                      agentData: { response: sanitizedContent },
                    };
                    console.log(lastMsg);
                    return [...preVal.slice(0, preVal.length - 1), lastMsg];
                  });
                } else {
                  if (parsed?.metadata?.agent_responses) {
                    const agent_responses = parsed?.metadata.agent_responses;
                    agent_responses?.forEach((agent_res: any) => {
                      if (agent_res.metadata?.tool_calls) {
                        tool_calls.push(...agent_res.metadata.tool_calls);
                      }
                    });

                    if (parsed?.citation_data) {
                      citations.push(...parsed.citation_data);
                    }

                    setMessages((preVal) => {
                      let lastMsg = preVal[preVal.length - 1];
                      lastMsg = {
                        ...lastMsg,
                        agentData: {
                          response: lastMsg.agentData?.response || "",
                          agent_responses: agent_responses,
                          metadata: { tool_calls: tool_calls },
                          citations: citations,
                        },
                      };
                      // console.log(lastMsg)
                      return [...preVal.slice(0, preVal.length - 1), lastMsg];
                    });
                  }
                }
              } catch (e) {
                console.error("Parse error:", jsonPart, e);
              }
            }
          }

          // Keep partial buffer (in case a line is split across chunks)
          buffer = lines[lines.length - 1];
        }
      } else {
        const resData = await fetch(
          `${TEAMS}/${teamsId}/execute?stream=false`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${GetSession()}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify(data),
            mode: "cors",
            credentials: "same-origin",
          }
        );

        if (!resData.ok) {
          throw new Error("Failed to response");
        }
        const responseData = await resData.json();
        setConversationId(responseData.conversation_id);
        const rawHtml: string = await marked.parse(responseData.response);
        const tool_calls: any[] = [];
        responseData?.agent_responses?.forEach((agent_res: any) => {
          if (agent_res.metadata?.tool_calls) {
            tool_calls.push(...agent_res.metadata.tool_calls);
          }
        });

        setMessages((preVal) => {
          return [
            ...preVal,
            {
              sender: PlayGroundEnum.AGENT,
              agentData: {
                response: rawHtml,
                agent_responses: responseData.agent_responses,
                metadata: { tool_calls: tool_calls },
              },
            },
          ];
        });
      }
    } catch (err) {
      console.error("Streaming failed:", err);
    } finally {
      setBotTyping(false);
    }
  };

  const getAgentShortCut = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(`${AGENT_SHORTCUT}`).then(
        (res) => res?.data
      );

      setAgentShortuts(data || []);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  };

  const clearChat = () => {
    setMessages([]);
    setUserInput("");
    setBotTyping(false);
    setShowInitialMessage(true);
    setShowQuestions(true);
  };

  const updateBubbleBg = (bubbleColor: string) => {
    setBubbleColor(bubbleColor);
  };
  const updateIncomingTextColor = (incomingTextColor: string) => {
    setIncomingTextColor(incomingTextColor);
  };
  const updateOutgoingTextColor = (outgoingTextColor: string) => {
    setOutgoingTextColor(outgoingTextColor);
  };
  const updateTitleColor = (titleColor: string) => {
    setTitleColor(titleColor);
  };
  const updateChatWindowBg = (color: string) => {
    setChatWindowColor(color);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [showHistory, setShowHistory] = React.useState(false);
  const handleViewRecentChats = () => {
    setShowHistory(true);
    handleClose();
  };
  const handleStartNewChat = () => {
    setShowHistory(false);
    handleClose();
  };
  const handleBackToChat = () => {
    setShowHistory(false);
  };

  const [showSettings, setShowSettings] = useState(false);

  // Smooth Scrolling to Bottom on New Message

  return (
    <Box
      sx={{
        height: "calc(100vh - 81px)",
        display: "flex",
        position: "relative",
        transition: "all 0.3s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          justifyContent: "left",
          position: "absolute",
          left: "26px",
          top: "9px",
          zIndex: 1,
        }}
      >
        <Button
          variant="text"
          startIcon={<KeyboardBackspaceIcon />}
          onClick={() => navigate("/agents", { state: { tabId: 1 } })}
          sx={{
            textTransform: "capitalize",
          }}
        >
          Back
        </Button>
        <Button
          size="small"
          variant="text"
          startIcon={<SettingsOutlinedIcon />}
          sx={{ textTransform: "capitalize" }}
          onClick={() => setShowSettings(true)}
        >
          Chat Interface Settings
        </Button>
      </Box>
      <Box
        sx={{
          width: "40%",
          background: "#fff",
          p: "32px",
          borderRight: "1px solid #E3E3E3",
          transition: "all 0.3s ease",
          //position: "relative"
          position: "absolute",
          top: 0,
          bottom: 0,
          left: showSettings ? 0 : "-40%",
        }}
      >
        <IconButton
          onClick={() => setShowSettings(false)}
          sx={{ position: "absolute", top: "3px", right: "21px" }}
        >
          <CloseIcon />
        </IconButton>
        <ChatInterfaceSettings
          chatIcon={chatIcon}
          setChatIcon={setChatIcon}
          profileImage={profileImage}
          setProfileImage={setProfileImage}
          updateBubbleColor={updateBubbleBg}
          updateIncomingTextColor={updateIncomingTextColor}
          updateOutgoingTextColor={updateOutgoingTextColor}
          updateTitleColor={updateTitleColor}
          updateChatWindowBg={updateChatWindowBg}
        />
      </Box>
      <Box
        sx={{
          width: showSettings ? "60%" : "100%",
          marginLeft: showSettings ? "40%" : "0",
          transition: "all 0.3s ease",
          background: "radial-gradient(circle, #f3f3f3 25%, transparent 26%)",
          backgroundSize: "1em 1em",
          backgroundColor: "#ffffff",
          opacity: "1",
          p: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: "660px",
            height: "94%",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              borderRadius: "30px",
              background: chatWindowColor,
              overflow: "hidden",
              boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
            }}
          >
            {showHistory ? (
              <RecentChats onBack={handleBackToChat} />
            ) : (
              <Box
                sx={{
                  height: "100%",
                }}
              >
                <Box
                  position="sticky"
                  sx={{
                    // backgroundColor: "#fff",
                    borderBottom: "1px solid #f9f7f7",
                    px: 4,
                    py: 1.5,
                    //height: "45px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    // boxShadow: "0 15px 30px 0 rgba(256, 256, 256, 1)",
                    zIndex: 1,
                    top: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={profileImage || LogoIcon}
                        alt="Company Logo"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        flexGrow: 1,
                        textTransform: "capitalize",
                        color: titleColor,
                      }}
                    >
                      {agents?.name || "Chat"}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      id="demo-positioned-button"
                      aria-controls={open ? "demo-positioned-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? "true" : undefined}
                      onClick={handleClick}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                    <Menu
                      id="demo-positioned-menu"
                      aria-labelledby="demo-positioned-button"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      PaperProps={{
                        elevation: 0, // Disable default MUI shadow
                        sx: {
                          boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
                          borderRadius: 2,
                        },
                      }}
                    >
                      <MenuItem onClick={handleStartNewChat}>
                        <ListItemIcon>
                          <NewChatIcon />
                        </ListItemIcon>
                        <ListItemText>Start a new chat</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={handleClose}>
                        <ListItemIcon>
                          <EndChatIcon />
                        </ListItemIcon>
                        <ListItemText>End chat</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={handleViewRecentChats}>
                        <ListItemIcon>
                          <RecentChatIcon />
                        </ListItemIcon>
                        <ListItemText>View recent chats</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
                {/* Chat Messages */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    height: "calc(100% - 160px)",
                    alignItems: "center",
                    pb: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      maxHeight: "100%",
                      overflow: "auto",
                      p: 4,
                      position: "relative",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0, 0, 0, 0.2)",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {},
                      "&::-webkit-scrollbar-track": {},
                    }}
                  >
                    {/* Main Text */}

                    {showInitialMessage && (
                      <>
                        <PlayGroundInitialMessage
                          message={intialMessage}
                          incomingTextColor={incomingTextColor}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            left: 0,
                            right: 0,
                            gap: 1,
                          }}
                        >
                          <img
                            src={LogoIcon}
                            alt="Company Logo"
                            style={{
                              width: "24px",
                              height: "auto",
                              objectFit: "cover",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: incomingTextColor }}
                          >
                            Powered by ZeaiQ
                          </Typography>
                        </Box>
                      </>
                    )}

                    {messages.map((msg, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                          mb: 3,
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          {/* Tool chips visible for all messages */}
                          {msg?.agentData?.metadata?.tool_calls &&
                            msg?.agentData?.metadata?.tool_calls?.length >
                            0 && (
                              <AgentPlayGroundTools
                                tools={msg?.agentData.metadata.tool_calls || []}
                              />
                            )}

                          {/* Bot Message */}
                          {msg.sender === PlayGroundEnum.AGENT ? (
                            <Box sx={{ display: "flex", width: "100%" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  mr: 2,
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                  //overflow: "hidden",
                                }}
                              >
                                <img
                                  src={profileImage || LogoIcon}
                                  alt="Company Logo"
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  width: "100%",
                                }}
                              >



                                {/* Main Text */}
                                <Typography
                                  key={index}
                                  variant="body2"
                                  component="div"
                                  sx={{
                                    color: incomingTextColor,
                                    whiteSpace: "normal",
                                    "& table": {
                                      borderCollapse: "collapse",
                                      width: "100%",
                                      margin: "8px 0",
                                    },
                                    "& th, & td": {
                                      border: "1px solid #bdbdbd",
                                      padding: "6px 10px",
                                    },
                                    "& th": {
                                      background: "#f5f5f5",
                                      fontWeight: 600,
                                    },
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: msg?.agentData?.response || "...",
                                  }}
                                />

                                {/* agent wise response */}
                                {/* {msg.agentData?.agent_responses && msg.agentData?.agent_responses?.map((agent_res) => (
                                  margin: "8px 0",
                                },
                                "& th, & td": {
                                  border: "1px solid #bdbdbd",
                                  padding: "6px 10px",
                                },
                                "& th": {
                                  background: "#f5f5f5",
                                  fontWeight: 600,
                                },
                              }}
                              dangerouslySetInnerHTML={{
                                __html: msg?.agentData?.response || "...",
                              }}
                            />
                            {/* Chart rendering */}
                                {msg?.agentData?.chartData && (
                                  <PlotlyChart
                                    data={msg.agentData.chartData.data || []}
                                    layout={msg.agentData.chartData.layout || {}}
                                    config={msg.agentData.chartData.config || { displayModeBar: false }}
                                  />
                                )}
                                <AgentPrompts
                                  agentShortuts={agentShortuts}
                                  messageId={index}
                                  sendMessageShortcuts={sendMessageShortcuts}
                                  incomingTextColor={incomingTextColor}
                                />

                                {/* References */}
                                <AgentPlaygroundReferences
                                  data={msg?.agentData?.citations}
                                  incomingTextColor={incomingTextColor}
                                />
                              </Box>
                            </Box>
                          ) : (
                            // User Message
                            <Box sx={{ display: "flex" }}>
                              <Avatar
                                variant="rounded"
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  bgcolor: variables.blackColor,
                                  mr: 2,
                                  p: "3px",
                                }}
                              >
                                <LineMdAccount />
                              </Avatar>
                              <Typography
                                variant="body2"
                                sx={{
                                  lineHeight: "24px",
                                  whiteSpace: "pre-wrap",
                                  color: outgoingTextColor,
                                }}
                                dangerouslySetInnerHTML={{ __html: msg?.userData?.input || "" }}
                              />
                              {/* {msg?.userData?.input} */}
                              {/* </Typography> */}
                            </Box>
                          )}
                        </Box>
                        <div ref={endOfMessagesRef} />
                      </Box>
                    ))}
                    {botTyping && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                          mb: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            mr: 2,
                            height: "24px",
                          }}
                        >
                          <img
                            src={LogoIcon}
                            alt="Company Logo"
                            style={{ width: 24, height: "auto" }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            height: 24,
                          }}
                        >
                          <TypingIndicator />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Input Field */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    p: 2,
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    width: "100%",
                    position: "sticky",
                    bottom: 0,
                    borderTop: "1px solid rgba(24, 24, 27, .1)",
                    flexDirection: "column",
                  }}
                >
                  {/* default questions */}
                  {showQuestions && teamsId && <AgentQuestions sendMessageQuestions={sendMessageQuestions} teamsId={teamsId} />}
                  <Box
                    sx={{
                      border: "1px solid rgba(24, 24, 27, .1)",
                      boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
                      borderRadius: "20px",
                      width: "100%",
                      position: "relative",
                      padding: "12px 16px",
                      background: "#fff",
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      variant="outlined"
                      size="small"
                      maxRows={4}
                      placeholder="Ask anything..."
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      inputRef={inputRef}
                      disabled={botTyping}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "14px",
                          maxHeight: "120px", // Add maximum height
                          overflowY: "auto", // Enable vertical scrolling
                          pr: "40px", // Space for the send button
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            borderRadius: "3px",
                          },
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={botTyping || !userInput}
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: bubbleColor,
                        color: variables.whiteColor,
                        minWidth: 0,
                        borderRadius: "50%",
                        position: "absolute",
                        bottom: "10px",
                        right: "10px",
                        "&.Mui-disabled": {
                          color: variables.whiteColor,
                          opacity: 0.5,
                        },
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 14L21 3m0 0l-6.5 18a.55.55 0 0 1-1 0L10 14l-7-3.5a.55.55 0 0 1 0-1z"
                        />
                      </svg>
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: "55px",
              height: "55px",
              borderRadius: "50%",
              backgroundColor: bubbleColor,
              marginLeft: "auto",
              marginTop: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
            }}
          >
            <img
              src={chatIcon || ChatIcon}
              alt="Chat Icon"
              style={{ width: 30, height: "auto" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MultiAgentPlayground;
